/**
 * Billing summary for step 4 — derived from the live authenticated session in the
 * Redux store (NOT a hardcoded demo session anymore).
 *
 * The server returns the full price breakdown on the active session's campaign; we
 * reuse the app's own `splitPrices` helper (the same one the config-driven Spark
 * configurator uses) so the split is identical:
 *   • setupFee        = the one-time base items (e.g. the "Setup" fee), discounted
 *   • productFeatures = the recurring gamification items, discounted
 *   • total           = setup + first month
 *
 * Earlier this read `price_evaluation.discountedPrice` directly and labelled it
 * "Product Features", which was wrong — that figure is the WHOLE campaign price
 * (setup + features), so the setup fee was double-counted.
 */

import { splitPrices } from '@/components/modules/spark/configurator/utils';
import type { ConfigReducer as SessionState, SessionVoucher } from '@/store/slices/session';

import { currencySymbol, formatAmount, formatAmountWithCode } from '../lib/currency';

export { formatAmount, formatAmountWithCode };

/** Outcome of applying or removing a voucher, derived from the backend-echoed `voucher` object. */
export interface VoucherResult {
  /** A valid discount is now applied to the order. */
  applied: boolean;
  /** The applied code when `applied`, else `null` (rejected or cleared). */
  code: string | null;
  /**
   * A machine reason when the code was rejected: the backend's `error` (e.g.
   * `VOUCHER_ALREADY_REDEEMED`), `'INVALID'` for a code that came back with no coverage,
   * `'NETWORK'` / `'UNAVAILABLE'` when the request never completed, or `null` on success / cleared.
   */
  error: string | null;
}

/**
 * Interpret a backend-echoed `voucher` object into an apply/remove outcome. `null`/absent (cleared)
 * → not applied, no error. A `code` with a positive `covered_amount` → applied. A code that came
 * back with zero coverage → rejected: its `error` when present, else a generic `'INVALID'`.
 * `covered_amount` may arrive as a string (e.g. `"39"`), same as {@link appliedVoucher}.
 */
export function interpretVoucher(voucher: SessionVoucher | null | undefined): VoucherResult {
  // Absent, or code-less (the echo after a remove) → simply "no voucher applied", never an error.
  if (!voucher || !voucher.code) return { applied: false, code: null, error: voucher?.error ?? null };
  const raw = voucher.covered_amount;
  const amount = typeof raw === 'string' ? Number(raw) : raw ?? 0;
  if (Number.isFinite(amount) && amount > 0) {
    return { applied: true, code: voucher.code, error: null };
  }
  // A code WAS submitted but covers nothing → a real rejection: its reason, else a generic invalid.
  return { applied: false, code: null, error: voucher.error ?? 'INVALID' };
}

export interface Billing {
  /** Currency code, e.g. "USD". */
  currency: string;
  /** Currency symbol for display. */
  symbol: string;
  /** One-time setup fee (discounted), from the campaign's base items. */
  setupFee: number;
  /** Monthly product-features price (discounted), from the gamification items. */
  productFeatures: number;
  /** Gross order total = setup + first month, BEFORE any voucher. Used as the analytics order value. */
  total: number;
  /** The applied voucher code (e.g. an affiliate code), or `null` when none is applied. */
  voucherCode: string | null;
  /** Amount the applied voucher covers off the first payment (0 when none). */
  voucherAmount: number;
  /** Net amount actually due today = max(0, total − voucherAmount). `0` when a voucher covers it all. */
  dueToday: number;
}

/**
 * True when the order carries no recurring monthly product-features fee, so the whole bill is a
 * single one-time charge (setup only). `false` while billing is still loading (we don't yet know).
 * Same predicate the summary uses to hide the "Product Features" row.
 */
export function isOneTimeBilling(billing: Billing | null): boolean {
  return billing != null && billing.productFeatures === 0;
}

/**
 * The applied voucher on the session, or a zeroed result when none is applied. The backend echoes
 * the voucher on the session's top-level `voucher` object; `covered_amount` sometimes arrives as a
 * string, and is `0` for an unknown / already-redeemed code (which we treat as "not applied").
 */
export function appliedVoucher(session: SessionState): { voucherCode: string | null; voucherAmount: number } {
  const voucher = session.voucher;
  const raw = voucher?.covered_amount;
  const amount = typeof raw === 'string' ? Number(raw) : raw ?? 0;
  if (voucher?.code && Number.isFinite(amount) && amount > 0) {
    return { voucherCode: voucher.code, voucherAmount: amount };
  }
  return { voucherCode: null, voucherAmount: 0 };
}

/** Step-4 billing depends on whether the user is on a real pack or the Custom pack. */
export interface BillingInputs {
  /** True when the selection deviates from the active pack's defaults (Custom pack). */
  isCustomPack: boolean;
  /** Catalog sum of the selected (non-Off) tier prices — used for the Custom pack. */
  catalogFeaturesTotal: number;
}

/**
 * Compute the step-4 billing breakdown = setup fee + features total.
 *  - Setup fee = the campaign's differentiated `price_evaluation.setupPrice` (via `baseItemsPrice`).
 *  - Product features = the CATALOG sum of the user's manual selection when on the Custom pack,
 *    else the active pack's `price_evaluation.featuresPrice` (via `itemsPrice`). Matches step-2.
 */
export function computeBilling(session: SessionState, currency: string, inputs: BillingInputs): Billing {
  const { baseItemsPrice, itemsPrice } = splitPrices(session, currency);
  const setupFee = baseItemsPrice.cost;
  const productFeatures = inputs.isCustomPack ? inputs.catalogFeaturesTotal : itemsPrice.cost;
  const total = setupFee + productFeatures;
  const { voucherCode, voucherAmount } = appliedVoucher(session);
  return {
    currency,
    symbol: currencySymbol(currency),
    setupFee,
    productFeatures,
    total,
    voucherCode,
    voucherAmount,
    dueToday: Math.max(0, total - voucherAmount),
  };
}
